/**
 * Where a signed-in person lands, as pure functions (ADR 0004: the active
 * tenant is part of the URL, /v/<slug>/…).
 *
 * `/` goes to the tenant used last if the person is still a member there, else
 * to the only membership, else to the picker. A platform manager without any
 * membership lands in the platform area. Old links without the prefix
 * (bookmarks from before tenancy) are forwarded to the same target.
 */
import type { Me, MembershipRef } from '@fsv/data';

export type HomeTarget =
  | { kind: 'tenant'; slug: string }
  | { kind: 'picker' }
  | { kind: 'platform' }
  /** Signed in, but no active membership and no platform role. */
  | { kind: 'none' };

type MeLike = Pick<Me, 'memberships' | 'isPlatformManager'>;

export function findMembership(me: MeLike, slug: string | undefined): MembershipRef | null {
  if (!slug) return null;
  return me.memberships.find((m) => m.slug === slug) ?? null;
}

export function resolveHome(me: MeLike, lastSlug: string | null): HomeTarget {
  const remembered = findMembership(me, lastSlug ?? undefined);
  if (remembered) return { kind: 'tenant', slug: remembered.slug };
  const [only, ...rest] = me.memberships;
  if (only && rest.length === 0) return { kind: 'tenant', slug: only.slug };
  if (only) return { kind: 'picker' };
  return me.isPlatformManager ? { kind: 'platform' } : { kind: 'none' };
}

/** `/v/<slug>` plus an in-tenant path; '/' is the fleet. */
export function tenantPath(slug: string, path = '/'): string {
  const base = `/v/${encodeURIComponent(slug)}`;
  if (!path || path === '/') return base;
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Same path in another tenant, for the switcher: stays on the same screen type, drops ids that belong to the old tenant. */
export function switchTenantPath(pathname: string, toSlug: string): string {
  const m = /^\/v\/[^/]+(\/.*)?$/.exec(pathname);
  const rest = m?.[1] ?? '/';
  // Aircraft, damages and admin sub-pages carry tenant-specific ids; land on the section instead.
  const section = /^\/(suche|eingang|profil|admin)(\/|$)/.exec(rest)?.[1];
  return tenantPath(toSlug, section ? `/${section}` : '/');
}

const LEGACY = /^\/(flugzeug|schaden|suche|eingang|admin)(\/|$)/;

/** Pre-tenancy URL → the same path under the home tenant, or null when there is no single target. */
export function legacyRedirect(pathname: string, search: string, me: MeLike, lastSlug: string | null): string | null {
  if (!LEGACY.test(pathname)) return null;
  const home = resolveHome(me, lastSlug);
  if (home.kind !== 'tenant') return null;
  return `${tenantPath(home.slug, pathname)}${search}`;
}
